
import UtilisationEmailControls from '../../components/UtilisationEmailControls';
import React, { useState, useEffect, useRef } from 'react';
import { Alert, Box, Tabs, Tab, Stack, TextField, Button, MenuItem } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GridTable from '../../components/GridTable';
import Layout from '../../components/Layout';
import { MODULES } from '../../utils/constants';
import AddBookingDialog from './AddBookingDialog';
import {
    COLUMNS,
    BOOKING_TYPES,
    EXTERNAL_APPOINTMENT_TYPES,
    EXTERNAL_BOOKING_INTEGRATIONS,
    EXTERNAL_BOOKING_PROVIDER_TYPES,
    getExternalBookingColumns
} from './constants';
import { useBookingStore } from '../../services/bookings';
import { BookingQueryParams, ExternalBookingLegType, IExternalBooking } from '../../types/bookings';
import dayjs from 'dayjs';
import {
    buildExternalBookingReportUrl,
    openExternalBookingReport,
    cancelExternalBooking,
    fetchExternalBookingById,
    fetchExternalBookingPaymentDetails,
    fetchExternalBookingReports,
    rescheduleExternalBooking,
    updateExternalBookingHealthCertificateRequired,
    updateFitnessCertificateVerification,
    updateExternalBookingSkipReport,
    uploadExternalBookingDocuments,
    useExternalBookingStore
} from '../../services/externalBookings';
import { useCorporateStore } from '../../services/corporates';
import ExternalBookingDetailsDialog from './ExternalBookingDetailsDialog';
import { ExternalBookingPaymentInfo } from '../../types/bookings';
import { showError } from '../../services/toaster';
import FieldSearch, { FieldSearchConfig } from '../../components/FieldSearch';
import { useAppContext } from '../../services/context/AppContext';
import { hasRole, isVendorUser } from '../../utils/helper';

type QuickDateFilter = '' | 'yesterday' | 'today' | 'tomorrow';
type ExternalEscalationFilter = '' | 'all' | 'pending' | 'report_overdue';

const QUICK_DATE_OPTIONS: { value: QuickDateFilter; label: string }[] = [
    { value: '', label: 'Custom / All Dates' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
];

// Filters owned by the header dropdowns/search in AddBookingDialog. The effects below
// rebuild the internal filter object from scratch, so these must be carried over.
const HEADER_FILTER_KEYS = [
    'search',
    'name',
    'email',
    'mobile',
    'userId',
    'specialistId',
    'wellnessPackageId',
    'serviceMode',
    'status',
    'paymentStatus',
];

const externalBookingSearchFields: FieldSearchConfig[] = [
    {
        fieldName: 'search',
        label: 'All',
        inputType: 'text',
        placeholder: 'Search by mobile, email, or name',
    },
    {
        fieldName: 'mobile',
        label: 'Mobile Number',
        inputType: 'tel',
        placeholder: 'Enter mobile number',
    },
    {
        fieldName: 'email',
        label: 'Email ID',
        inputType: 'email',
        placeholder: 'Enter email ID',
    },
    {
        fieldName: 'name',
        label: 'Name',
        inputType: 'text',
        placeholder: 'Enter name',
    },
];

const Bookings = () => {
    const { userData } = useAppContext();
    const isVendor = isVendorUser(userData);
    const canToggleSkipReport =
        hasRole(userData?.role, MODULES.ADMIN) ||
        hasRole(userData?.backendRole, MODULES.ADMIN) ||
        hasRole(userData?.role, MODULES.HR) ||
        hasRole(userData?.backendRole, MODULES.HR);
    const canVerifyFitnessCertificate =
        hasRole(userData?.role, MODULES.ADMIN) ||
        hasRole(userData?.backendRole, MODULES.ADMIN);
    const isAdmin =
        hasRole(userData?.role, MODULES.ADMIN) ||
        hasRole(userData?.backendRole, MODULES.ADMIN);
    const {
        data,
        totalPages,
        currentPage,
        total,
        isLoading,
        fetchGrid,
        onPageChange: storePageChange,
        onDelete,
        limit,
        filters,
        setFilters,
        exportBookings: exportInternalBookings
    } = useBookingStore();
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const {
        filters: externalFilters,
        data: externalData,
        totalPages: externalTotalPages,
        currentPage: externalCurrentPage,
        total: externalTotal,
        isLoading: externalLoading,
        onPageChange: onExternalPageChange,
        limit: externalLimit,
        setFilters: setExternalFilters,
        exportBookings: exportExternalBookings
    } = useExternalBookingStore();
    const { allData: corporateData, fetchAll: fetchAllCorporates } = useCorporateStore();

    const [bookingMode, setBookingMode] = useState<'internal' | 'external'>(isVendor ? 'external' : 'internal');
    const [tabValue, setTabValue] = useState('all');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const invalidCreationRange = Boolean(createdFrom && createdTo && createdFrom > createdTo);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [quickDateFilter, setQuickDateFilter] = useState<QuickDateFilter>('');
    const [selectedCorporateId, setSelectedCorporateId] = useState<string>('');
    const [externalTabValue, setExternalTabValue] = useState('DOCTOR');
    const [externalFromDate, setExternalFromDate] = useState<string>('');
    const [externalToDate, setExternalToDate] = useState<string>('');
    const [externalQuickDateFilter, setExternalQuickDateFilter] = useState<QuickDateFilter>('');
    const [externalIntegration, setExternalIntegration] = useState('');
    const [externalStatus, setExternalStatus] = useState<string>('');
    const [externalPaymentStatus, setExternalPaymentStatus] = useState<string>('');
    const [externalAppointmentType, setExternalAppointmentType] = useState<string>('');
    const [externalBeneficiary, setExternalBeneficiary] = useState<string>('');
    const [externalEscalation, setExternalEscalation] = useState<ExternalEscalationFilter>('');
    const [externalSearchFilters, setExternalSearchFilters] = useState<Record<string, string>>({});
    const [externalSearchResetKey, setExternalSearchResetKey] = useState(0);
    const [selectedExternalBooking, setSelectedExternalBooking] = useState<IExternalBooking | null>(null);
    const selectedExternalBookingIdRef = useRef<string | undefined>();
    const externalReportFetchRun = useRef(0);
    selectedExternalBookingIdRef.current = selectedExternalBooking?._id;
    useEffect(() => () => { selectedExternalBookingIdRef.current = undefined; externalReportFetchRun.current += 1; }, []);
    const [selectedExternalBookingPayment, setSelectedExternalBookingPayment] = useState<ExternalBookingPaymentInfo | null>(null);
    const [selectedExternalBookingReports, setSelectedExternalBookingReports] = useState<string[]>([]);
    const [externalBookingPaymentLoading, setExternalBookingPaymentLoading] = useState(false);
    const [isExportingInternal, setIsExportingInternal] = useState(false);
    const [isExportingExternal, setIsExportingExternal] = useState(false);

    useEffect(() => {
        fetchAllCorporates();
    }, [fetchAllCorporates]);

    useEffect(() => {
        if (isVendor && bookingMode !== 'external') {
            setBookingMode('external');
        }
    }, [bookingMode, isVendor]);

    const applyQuickDateFilter = (filter: QuickDateFilter) => {
        setQuickDateFilter(filter);

        if (!filter) {
            setFromDate('');
            setToDate('');
            return;
        }

        const targetDate = dayjs().add(
            filter === 'yesterday' ? -1 : filter === 'tomorrow' ? 1 : 0,
            'day'
        ).format('YYYY-MM-DD');

        setFromDate(targetDate);
        setToDate(targetDate);
    };

    const applyExternalQuickDateFilter = (filter: QuickDateFilter) => {
        setExternalQuickDateFilter(filter);

        if (!filter) {
            setExternalFromDate('');
            setExternalToDate('');
            return;
        }

        const targetDate = dayjs().add(
            filter === 'yesterday' ? -1 : filter === 'tomorrow' ? 1 : 0,
            'day'
        ).format('YYYY-MM-DD');

        setExternalFromDate(targetDate);
        setExternalToDate(targetDate);
    };

    useEffect(() => {
        if (bookingMode !== 'internal' || invalidCreationRange) return;

        const currentFilters = filtersRef.current as Record<string, any>;
        const newFilters: BookingQueryParams = {};

        HEADER_FILTER_KEYS.forEach((key) => {
            if (currentFilters[key]) {
                newFilters[key] = currentFilters[key];
            }
        });

        if (tabValue !== 'all') {
            newFilters.bookingType = tabValue as any;
        }

        if (tabValue !== 'consultation') {
            delete newFilters.specialistId;
        }
        if (tabValue !== 'package') {
            delete newFilters.wellnessPackageId;
        }

        if (selectedCorporateId) {
            newFilters.corporateId = selectedCorporateId;
        }

        if (fromDate) {
            newFilters.bookingDateFrom = fromDate.replace(/-/g, '');
        }
        if (toDate) {
            newFilters.bookingDateTo = toDate.replace(/-/g, '');
        }

        if (createdFrom) newFilters.createdFrom = createdFrom;
        if (createdTo) newFilters.createdTo = createdTo;
        setFilters(newFilters);
    }, [bookingMode, tabValue, selectedCorporateId, fromDate, toDate, createdFrom, createdTo, invalidCreationRange, setFilters]);

    useEffect(() => {
        if (bookingMode !== 'external' || invalidCreationRange) return;

        const newFilters: Record<string, string> = { providerType: externalTabValue };

        if (!isVendor && externalIntegration) {
            newFilters.integration = externalIntegration;
        }
        if (externalStatus) {
            newFilters.status = externalStatus;
        }
        if (externalPaymentStatus) {
            newFilters.paymentStatus = externalPaymentStatus;
        }
        if (externalAppointmentType) {
            newFilters.appointmentType = externalAppointmentType;
        }
        if (externalBeneficiary) {
            newFilters.beneficiary = externalBeneficiary;
        }
        if (isAdmin && externalTabValue === 'DIAGNOSTICS' && externalEscalation) {
            newFilters.escalation = externalEscalation;
        }
        if (selectedCorporateId) {
            newFilters.corporateId = selectedCorporateId;
        }
        externalBookingSearchFields.forEach((field) => {
            const value = externalSearchFilters[field.fieldName]?.trim();
            if (value) {
                newFilters[field.fieldName] = value;
            }
        });
        if (externalFromDate) {
            newFilters.bookingDateFrom = externalFromDate.replace(/-/g, '');
        }
        if (externalToDate) {
            newFilters.bookingDateTo = externalToDate.replace(/-/g, '');
        }

        if (createdFrom) newFilters.createdFrom = createdFrom;
        if (createdTo) newFilters.createdTo = createdTo;
        setExternalFilters(newFilters);
    }, [
        bookingMode,
        externalTabValue,
        isVendor,
        externalIntegration,
        externalStatus,
        externalPaymentStatus,
        externalAppointmentType,
        externalBeneficiary,
        externalEscalation,
        externalSearchFilters,
        isAdmin,
        selectedCorporateId,
        externalFromDate,
        externalToDate,
        createdFrom,
        createdTo,
        invalidCreationRange,
        setExternalFilters
    ]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
    };

    const handleInternalExport = async () => {
        setIsExportingInternal(true);
        try {
            await exportInternalBookings();
        } finally {
            setIsExportingInternal(false);
        }
    };

    const handleExternalExport = async () => {
        setIsExportingExternal(true);
        try {
            await exportExternalBookings();
        } finally {
            setIsExportingExternal(false);
        }
    };

    const handleExternalBookingSearch = (searchValues: Record<string, string>) => {
        setExternalSearchFilters(searchValues);
    };

    const handleExternalBookingView = async (booking: IExternalBooking) => {
        externalReportFetchRun.current += 1;
        setSelectedExternalBooking(booking);
        setSelectedExternalBookingPayment(null);
        setExternalBookingPaymentLoading(true);

        try {
            const bookingDetails = await fetchExternalBookingById(booking._id);
            if (bookingDetails) {
                setSelectedExternalBooking(bookingDetails);
            }
            const paymentDetails = isVendor ? null : await fetchExternalBookingPaymentDetails(booking._id);
            const reportDetails =
                Array.isArray((bookingDetails || booking).report) && (bookingDetails || booking).report?.length
                    ? { report: (bookingDetails || booking).report }
                    : await fetchExternalBookingReports(booking._id);
            setSelectedExternalBookingPayment(paymentDetails);
            setSelectedExternalBookingReports(
                Array.isArray(reportDetails?.report)
                    ? (reportDetails?.report ?? []).map((reportUrl) => buildExternalBookingReportUrl(reportUrl))
                    : []
            );
        } finally {
            setExternalBookingPaymentLoading(false);
        }
    };

    const handleExternalFetchReports = async () => {
        if (!selectedExternalBooking) return;
        const id = selectedExternalBooking._id;
        const run = ++externalReportFetchRun.current;
        const result = await fetchExternalBookingReports(id, true);
        if (!result) throw new Error('Unable to fetch reports');
        if (selectedExternalBookingIdRef.current !== id || externalReportFetchRun.current !== run) return;
        setSelectedExternalBookingReports(result.report.map(buildExternalBookingReportUrl));
        const updated = await fetchExternalBookingById(id);
        if (updated && selectedExternalBookingIdRef.current === id && externalReportFetchRun.current === run) setSelectedExternalBooking(updated);
        return result;
    };

    const handleExternalBookingReport = async (booking: IExternalBooking) => {
        const reportPaths = Array.isArray(booking.report) && booking.report.length
            ? booking.report
            : (await fetchExternalBookingReports(booking._id))?.report || [];

        if (!reportPaths.length) {
            showError('No reports available for this booking');
            return;
        }

        try {
            await Promise.all(reportPaths.map((reportUrl) => openExternalBookingReport(reportUrl)));
        } catch {
            showError('Unable to open one or more reports');
        }
    };

    const handleExternalCancel = async (reason: string) => {
        if (!selectedExternalBooking) return;
        const updated = await cancelExternalBooking(selectedExternalBooking, reason);
        if (updated) {
            setSelectedExternalBooking(updated);
        }
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    const handleExternalReschedule = async (payload: { bookingDate: string; bookingTime: string; leg?: ExternalBookingLegType }) => {
        if (!selectedExternalBooking) return;
        const updated = await rescheduleExternalBooking(selectedExternalBooking, payload);
        if (!updated) throw new Error('Reschedule failed. Please check the selected visit and try again.');
        setSelectedExternalBooking(updated);
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    const handleExternalDocumentUpload = async (
        type: 'report' | 'other' | 'fitness_certificate',
        files: File[],
        leg?: ExternalBookingLegType
    ) => {
        if (!selectedExternalBooking) return;
        const result = await uploadExternalBookingDocuments(selectedExternalBooking._id, type, files, leg);
        if (!result?.booking) throw new Error('Failed to upload booking document');
        setSelectedExternalBooking(result.booking);
        const report = result?.report;
        if (Array.isArray(report)) {
            setSelectedExternalBookingReports(report.map((reportUrl) => buildExternalBookingReportUrl(reportUrl)));
        }
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    const handleExternalSkipReport = async (booking: IExternalBooking, skipReport: boolean) => {
        const updated = await updateExternalBookingSkipReport(booking._id, skipReport);
        if (updated && updated._id === selectedExternalBooking?._id) {
            setSelectedExternalBooking(updated);
            setSelectedExternalBookingReports(
                updated.skipReport
                    ? []
                    : (updated.report || []).map((reportUrl) => buildExternalBookingReportUrl(reportUrl))
            );
        }
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    const handleExternalHealthCertificate = async (
        booking: IExternalBooking,
        skipHealthCertificate: boolean
    ) => {
        const updated = await updateExternalBookingHealthCertificateRequired(
            booking._id,
            skipHealthCertificate
        );
        if (updated && updated._id === selectedExternalBooking?._id) {
            setSelectedExternalBooking(updated);
        }
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    const handleFitnessCertificateVerification = async (verified: boolean) => {
        if (!selectedExternalBooking) return;
        const updated = await updateFitnessCertificateVerification(selectedExternalBooking._id, verified);
        if (!updated) throw new Error('Failed to update fitness certificate verification');
        setSelectedExternalBooking(updated);
        await onExternalPageChange(null, externalCurrentPage - 1);
    };

    return (
        <Layout appBarTitle="Bookings Management">
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }} useFlexGap>
                <TextField label="Booking created from (IST)" sx={{ minWidth: 220 }} type="date" size="small" InputLabelProps={{ shrink: true }} value={createdFrom} onChange={event => setCreatedFrom(event.target.value)} inputProps={{ max: createdTo || undefined }} error={invalidCreationRange} />
                <TextField label="Booking created to (IST)" sx={{ minWidth: 220 }} type="date" size="small" InputLabelProps={{ shrink: true }} value={createdTo} onChange={event => setCreatedTo(event.target.value)} inputProps={{ min: createdFrom || undefined }} error={invalidCreationRange} helperText={invalidCreationRange ? 'End date must be on or after start date.' : undefined} />
                <Button onClick={() => { setCreatedFrom(''); setCreatedTo(''); }}>Clear creation dates</Button>
            </Stack>
            <UtilisationEmailControls source={bookingMode} filters={bookingMode === 'internal' ? filters : externalFilters} disabled={(bookingMode === 'internal' ? isLoading : externalLoading) || invalidCreationRange} />
            {!isVendor && <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', mb: 2 }}>
                <Tabs value={bookingMode} onChange={(_, value) => setBookingMode(value)} aria-label="booking mode tabs">
                    <Tab label="Internal Bookings" value="internal" />
                    <Tab label="External Bookings" value="external" />
                </Tabs>
            </Box>}

            {bookingMode === 'internal' && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', mb: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="booking type tabs">
                        <Tab label="All Bookings" value="all" />
                        {BOOKING_TYPES.map((type) => (
                            <Tab key={type.value} label={type.label} value={type.value} />
                        ))}
                    </Tabs>
                </Box>
            )}

            {bookingMode === 'internal' && (
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                    <TextField
                        select
                        label="Quick Date"
                        size="small"
                        value={quickDateFilter}
                        onChange={(e) => applyQuickDateFilter(e.target.value as QuickDateFilter)}
                        sx={{ minWidth: 180 }}
                    >
                        {QUICK_DATE_OPTIONS.map((option) => (
                            <MenuItem key={option.value || 'custom'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Appointment from"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) => {
                            setQuickDateFilter('');
                            setFromDate(e.target.value);
                        }}
                    />
                    <TextField
                        label="Appointment to"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) => {
                            setQuickDateFilter('');
                            setToDate(e.target.value);
                        }}
                    />
                    {!isVendor && <TextField
                        select
                        label="Corporate"
                        size="small"
                        value={selectedCorporateId}
                        onChange={(e) => setSelectedCorporateId(e.target.value)}
                        sx={{ minWidth: 220 }}
                    >
                        <MenuItem value="">All Corporates</MenuItem>
                        {corporateData.map((corporate: any) => (
                            <MenuItem key={corporate._id} value={corporate._id}>
                                {corporate.companyName || corporate.name}
                            </MenuItem>
                        ))}
                    </TextField>}
                    {(selectedCorporateId || fromDate || toDate || createdFrom || createdTo) && (
                        <Button
                            size="small"
                            color="inherit"
                            variant="text"
                            onClick={() => {
                                setSelectedCorporateId('');
                                setCreatedFrom('');
                                setCreatedTo('');
                                applyQuickDateFilter('');
                            }}
                        >
                            Clear Filters
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleInternalExport}
                        disabled={isExportingInternal || isLoading || invalidCreationRange}
                    >
                        {isExportingInternal ? 'Exporting...' : 'Export Bookings'}
                    </Button>
                </Stack>
            )}

            {bookingMode === 'internal' && <Layout.Header component={AddBookingDialog} props={{ module: MODULES.BOOKING }} />}

            {bookingMode === 'internal' && (
                <Layout.Body
                    component={GridTable}
                    props={{
                        data,
                        columns: COLUMNS,
                        currentPage,
                        totalPages,
                        total,
                        loading: isLoading,
                        onPageChange: storePageChange,
                        module: MODULES.BOOKING,
                        onDelete: (item: any) => onDelete(item._id),
                        rows: limit
                    }}
                />
            )}

            {bookingMode === 'external' && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', mb: 2 }}>
                    <Tabs
                        value={externalTabValue}
                        onChange={(_, value) => {
                            setExternalTabValue(value);
                            if (value !== 'DIAGNOSTICS') setExternalEscalation('');
                        }}
                        aria-label="external booking provider tabs"
                    >
                        {EXTERNAL_BOOKING_PROVIDER_TYPES.map((type) => (
                            <Tab key={type.value} label={type.label} value={type.value} />
                        ))}
                    </Tabs>
                </Box>
            )}

            {bookingMode === 'external' && (
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                    <TextField
                        select
                        label="Quick Date"
                        size="small"
                        value={externalQuickDateFilter}
                        onChange={(e) => applyExternalQuickDateFilter(e.target.value as QuickDateFilter)}
                        sx={{ minWidth: 180 }}
                    >
                        {QUICK_DATE_OPTIONS.map((option) => (
                            <MenuItem key={option.value || 'custom'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <FieldSearch
                        key={externalSearchResetKey}
                        fields={externalBookingSearchFields}
                        onSearch={handleExternalBookingSearch}
                    />

                    {!isVendor && (
                        <TextField
                            select
                            label="Integration"
                            size="small"
                            value={externalIntegration}
                            onChange={(event) => setExternalIntegration(event.target.value)}
                            sx={{ minWidth: 170 }}
                        >
                            <MenuItem value="">All Integrations</MenuItem>
                            {EXTERNAL_BOOKING_INTEGRATIONS.map((integration) => (
                                <MenuItem key={integration.value} value={integration.value}>
                                    {integration.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField
                        select
                        label="Order Status"
                        size="small"
                        value={externalStatus}
                        onChange={(e) => {
                            setExternalStatus(e.target.value);
                            if (e.target.value) setExternalEscalation('');
                        }}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="abandoned">Abandoned</MenuItem>
                        <MenuItem value="report_delivered">Report Delivered</MenuItem>
                    </TextField>

                    {isAdmin && externalTabValue === 'DIAGNOSTICS' && (
                        <TextField
                            select
                            label="Escalations"
                            size="small"
                            value={externalEscalation}
                            onChange={(event) => {
                                setExternalEscalation(event.target.value as ExternalEscalationFilter);
                                if (event.target.value) {
                                    setExternalStatus('');
                                    setExternalPaymentStatus('');
                                }
                            }}
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="">All Bookings</MenuItem>
                            <MenuItem value="all">All Escalations</MenuItem>
                            <MenuItem value="pending">Pending Confirmation</MenuItem>
                            <MenuItem value="report_overdue">Report Overdue (Previous Day+)</MenuItem>
                        </TextField>
                    )}

                    {!isVendor && (
                        <TextField
                            select
                            label="Payment"
                            size="small"
                            value={externalPaymentStatus}
                            onChange={(e) => {
                                setExternalPaymentStatus(e.target.value);
                                if (e.target.value) setExternalEscalation('');
                            }}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="refunded">Refunded</MenuItem>
                        </TextField>
                    )}

                    <TextField
                        select
                        label="Appointment"
                        size="small"
                        value={externalAppointmentType}
                        onChange={(e) => setExternalAppointmentType(e.target.value)}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {EXTERNAL_APPOINTMENT_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Beneficiary"
                        size="small"
                        value={externalBeneficiary}
                        onChange={(e) => setExternalBeneficiary(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="employee">Employee</MenuItem>
                        <MenuItem value="dependent">Dependent</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="Corporate"
                        size="small"
                        value={selectedCorporateId}
                        onChange={(e) => setSelectedCorporateId(e.target.value)}
                        sx={{ minWidth: 220 }}
                    >
                        <MenuItem value="">All Corporates</MenuItem>
                        {corporateData.map((corporate: any) => (
                            <MenuItem key={corporate._id} value={corporate._id}>
                                {corporate.companyName || corporate.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Appointment from"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={externalFromDate}
                        onChange={(e) => {
                            setExternalQuickDateFilter('');
                            setExternalFromDate(e.target.value);
                        }}
                    />

                    <TextField
                        label="Appointment to"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={externalToDate}
                        onChange={(e) => {
                            setExternalQuickDateFilter('');
                            setExternalToDate(e.target.value);
                        }}
                    />

                    {(selectedCorporateId || Object.keys(externalSearchFilters).length > 0 || externalIntegration || externalStatus || externalPaymentStatus || externalAppointmentType || externalBeneficiary || externalEscalation || externalFromDate || externalToDate || createdFrom || createdTo) && (
                        <Button
                            size="small"
                            color="inherit"
                            variant="text"
                            onClick={() => {
                                setExternalSearchFilters({});
                                setExternalSearchResetKey((value) => value + 1);
                                setExternalIntegration('');
                                setExternalStatus('');
                                setExternalPaymentStatus('');
                                setExternalAppointmentType('');
                                setExternalBeneficiary('');
                                setExternalEscalation('');
                                setSelectedCorporateId('');
                                setCreatedFrom('');
                                setCreatedTo('');
                                applyExternalQuickDateFilter('');
                            }}
                        >
                            Clear Filters
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleExternalExport}
                        disabled={isExportingExternal || externalLoading || invalidCreationRange}
                    >
                        {isExportingExternal ? 'Exporting...' : 'Export External Bookings'}
                    </Button>
                </Stack>
            )}

            {bookingMode === 'external' && isAdmin && externalTabValue === 'DIAGNOSTICS' && externalEscalation && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                    <strong>{externalTotal} booking{externalTotal === 1 ? '' : 's'} need attention.</strong>{' '}
                    {externalEscalation === 'pending'
                        ? 'Showing paid bookings still awaiting provider confirmation.'
                        : externalEscalation === 'report_overdue'
                            ? 'Showing paid diagnostics from a previous appointment date with reports still missing. Hybrid visits are checked per leg.'
                            : 'Showing paid bookings awaiting confirmation or an overdue report. Hybrid visits are checked per leg.'}
                </Alert>
            )}

            {bookingMode === 'external' && (
                <Layout.Body
                    component={GridTable}
                    props={{
                        data: externalData,
                        columns: getExternalBookingColumns(handleExternalBookingView, handleExternalBookingReport, {
                            hideFinancials: isVendor,
                            onToggleSkipReport: canToggleSkipReport
                                ? (booking, skipReport) => void handleExternalSkipReport(booking, skipReport)
                                : undefined,
                            onToggleHealthCertificate: canToggleSkipReport
                                ? (booking, skipHealthCertificate) => void handleExternalHealthCertificate(booking, skipHealthCertificate)
                                : undefined
                        }),
                        currentPage: externalCurrentPage,
                        totalPages: externalTotalPages,
                        total: externalTotal,
                        loading: externalLoading,
                        onPageChange: onExternalPageChange,
                        module: MODULES.NOTIFICATION,
                        rows: externalLimit,
                        hideAction: true
                    }}
                />
            )}

            <ExternalBookingDetailsDialog
                open={Boolean(selectedExternalBooking)}
                booking={selectedExternalBooking}
                paymentDetails={selectedExternalBookingPayment}
                    reports={selectedExternalBookingReports}
                    onFetchReports={handleExternalFetchReports}
                    loading={externalBookingPaymentLoading}
                    canManage={isVendor}
                    onCancelBooking={handleExternalCancel}
                    onRescheduleBooking={handleExternalReschedule}
                    onUploadDocuments={handleExternalDocumentUpload}
                    onToggleSkipReport={canToggleSkipReport
                        ? (skipReport) => selectedExternalBooking ? handleExternalSkipReport(selectedExternalBooking, skipReport) : Promise.resolve()
                        : undefined}
                    onToggleHealthCertificate={canToggleSkipReport
                        ? (skipHealthCertificate) => selectedExternalBooking ? handleExternalHealthCertificate(selectedExternalBooking, skipHealthCertificate) : Promise.resolve()
                        : undefined}
                    onToggleFitnessCertificateVerification={canVerifyFitnessCertificate
                        ? handleFitnessCertificateVerification
                        : undefined}
                    onClose={() => {
                    externalReportFetchRun.current += 1;
                    setSelectedExternalBooking(null);
                    setSelectedExternalBookingPayment(null);
                    setSelectedExternalBookingReports([]);
                    setExternalBookingPaymentLoading(false);
                }}
            />
        </Layout>
    );
};

export default Bookings;
